import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Divider,
  CircularProgress,
  Stack,
  IconButton,
  Tooltip,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import HistoryIcon from "@mui/icons-material/History";
import { useIntl } from "react-intl";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useNotification } from "../../providers/NotificationProvider";
import { fetchWorkerDetail } from "../../api/memberApi";

function DetailRow({ label, value, onZoomIn, zoomInTooltip }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
      <Typography
        variant="body2"
        sx={{ width: 160, flexShrink: 0, color: "text.secondary", fontWeight: 500 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{ flexGrow: 1 }}>{value || "—"}</Typography>
      {onZoomIn && value && (
        <Tooltip title={zoomInTooltip || ""}>
          <IconButton size="small" onClick={onZoomIn}>
            <ZoomInIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

export default function WorkerDetailPage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { workerId } = useParams();
  const location = useLocation();
  const notify = useNotification();

  // 解任済みタブからの遷移時に state.released = true が渡される
  const isReleased = location.state?.released === true;

  const [worker, setWorker] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadWorker = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchWorkerDetail(workerId);
      setWorker(data);
    } catch (err) {
      notify.apiError(err);
    } finally {
      setLoading(false);
    }
  }, [workerId, notify]);

  useEffect(() => {
    loadWorker();
  }, [loadWorker]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!worker) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/workers")}
          sx={{ mb: 2 }}
        >
          {intl.formatMessage({ id: "common.back" })}
        </Button>
        <Typography color="text.secondary">
          {intl.formatMessage({ id: "worker.notFound" })}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* ヘッダー */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/workers")}
          variant="text"
        >
          {intl.formatMessage({ id: "common.back" })}
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {worker.workerName}
        </Typography>
        {isReleased && (
          <Chip
            label={intl.formatMessage({ id: "worker.released" })}
            color="default"
            size="small"
          />
        )}
      </Box>

      {/* 詳細情報 */}
      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {intl.formatMessage({ id: "worker.detail.title" })}
        </Typography>
        <Divider sx={{ mb: 1 }} />
        <DetailRow
          label={intl.formatMessage({ id: "worker.name" })}
          value={worker.workerName}
        />
        {worker.workerDescription && (
          <DetailRow
            label={intl.formatMessage({ id: "worker.description" })}
            value={worker.workerDescription}
          />
        )}
        <DetailRow
          label={intl.formatMessage({ id: "worker.project" })}
          value={worker.projectName}
          onZoomIn={() => navigate(`/workers/${workerId}/project-goal-achievements`)}
          zoomInTooltip={intl.formatMessage({ id: "worker.project.goalAchievement" })}
        />
        <DetailRow
          label={intl.formatMessage({ id: "worker.program" })}
          value={worker.programName}
          onZoomIn={() => navigate(`/workers/${workerId}/program-goal-achievements`)}
          zoomInTooltip={intl.formatMessage({ id: "worker.program.goalAchievement" })}
        />
        <DetailRow
          label={intl.formatMessage({ id: "worker.department" })}
          value={worker.departmentName}
        />
      </Paper>

      {/* ナビゲーションボタン */}
      <Divider sx={{ mb: 3 }} />
      <Stack direction="row" spacing={2} flexWrap="wrap">
        {!isReleased && (
          <Button
            variant="outlined"
            startIcon={<AccountTreeIcon />}
            onClick={() => navigate(`/workers/${workerId}/workflows`)}
          >
            {intl.formatMessage({ id: "worker.workflows" })}
          </Button>
        )}
        {!isReleased && (
          <Button
            variant="outlined"
            startIcon={<TrackChangesIcon />}
            onClick={() => navigate(`/workers/${workerId}/goals`)}
          >
            {intl.formatMessage({ id: "worker.goals" })}
          </Button>
        )}
        {!isReleased && (
          <Button
            variant="outlined"
            startIcon={<EmojiEventsIcon />}
            onClick={() => navigate(`/workers/${workerId}/achievements`)}
          >
            {intl.formatMessage({ id: "worker.achievements" })}
          </Button>
        )}
        {isReleased && (
          <Button
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={() => navigate(`/workers/${workerId}/history`)}
          >
            {intl.formatMessage({ id: "worker.workHistory" })}
          </Button>
        )}
      </Stack>
    </Box>
  );
}
